
import { Component, Vue, Prop } from "vue-property-decorator";
import Video from "@/lib/common/video";
import PermissionPage from "./permissionDenial.vue";
import "../assets/js/tracking-min.js";
import "../assets/js/face-min.js";

interface FaceModel {
  x: number; // x 坐标
  y: number; // y 坐标
  width: number; // 宽度
  height: number; // 长度
  total: number; // 总数量
}

@Component({
  components: {
    PermissionPage
  }
})
/**
 * 1. 支持 getUserMedia：
 *  -> 使用【video + canvas】
 *    -> 使用 tracking.js 检测人脸是否在框中，自动拍照，然后上传照片，调用接口 /v1/component/exec 核验
 * 2.
 */
export default class ComUserMediaVerify extends Vue {
  @Prop({ default: false }) private permission!: boolean;
  canvas: any = null;
  canvasContext: any = null;
  videoElement: any = null;
  videoWidth = 375;
  videoHeight = 375;
  canvasWidth = 375;
  canvasHeight = 375;
  imgUrl: any = "";
  file: any = null;
  imgData: any = null;

  streamIns: any = null; // 视频流

  tracker: any = null;
  trackerTask: any = null;
  scanTip = ""; // 提示信息
  findFace = false; // 是否检测到人脸
  removePhotoID = 0; // 停止转换图片

  init() {
    this.initElement();
    this.initCanvasVideoSize();
  }

  initElement() {
    this.canvas = this.$refs.cameraCanvas as HTMLCanvasElement;
    this.canvasContext = this.canvas?.getContext("2d");
    this.videoElement = this.$refs.video as HTMLVideoElement;
  }
  initCanvasVideoSize() {
    const scale = window.screen.width / 375;
    // TODO: 调整高度之后 canvas 画的图片宽度压缩了，这个问题待查找
    // const heightScale = 1380 / 1125; // 高度按照给定的 capture-img 图片的高度调整
    const heightScale = 1;
    this.videoWidth = 375 * scale;
    this.videoHeight = 375 * scale * heightScale;
    this.canvasWidth = 375 * scale;
    this.canvasHeight = 375 * scale * heightScale;
  }

  initTracker() {
    // 初始化之前先 清除 tracker；
    this.clearTracker();
    this.initElement();
    try {
      this.tracker = new window.tracking.ObjectTracker("face"); // tracker实例
      this.tracker.setInitialScale(4);
      this.tracker.setStepSize(2); // 设置步长
      this.tracker.setEdgesDensity(0.1);
      this.trackerTask = window.tracking.track("#video", this.tracker); // 开始追踪
    } catch (e) {
      this.scanTip = "访问用户媒体失败，请重试";
    }
    //开始捕捉方法 一直不停的检测人脸直到检测到人脸
    this.tracker.on("track", (e: any) => {
      //画布描绘之前清空画布
      this.clearCanvas();
      if (e?.data?.length === 0) {
        this.scanTip = "未检测到人脸";
      } else if (e?.data?.length > 1) {
        this.scanTip = "检测到多张人脸";
      } else {
        // 检测到一个人脸
        e?.data?.forEach((rect: FaceModel) => {
          // 测试使用：设置canvas 方框的颜色大小
          // this.canvasContext.strokeStyle = "#42e365";
          // this.canvasContext.lineWidth = 2;
          // this.canvasContext.strokeRect(rect.x, rect.y, rect.width, rect.height);
          // console.log('rect', rect)
          if (this.checkFacePosition(rect)) {
            this.setCaptureStatus(this.getCaptureStatus().SUCCESS);
            // console.log("rect success", rect);
            this.scanTip = "检测成功，正在拍照，请保持不动";
            // findFace 限制一直捕捉人脸，只要拍照之后就停止检测
            if (!this.findFace) {
              this.scanTip = "拍照中...";
              this.findFace = true;
              this.removePhotoID = setTimeout(() => {
                this.userMediaFaceVerify();
                this.clearTracker();
              });
            }
          } else {
            this.setCaptureStatus(this.getCaptureStatus().ERROR);
          }
        });
      }
    });
  }

  /** =============== 捕捉框相关 start ============== */

  captureStatus = this.getCaptureStatus().INFO; // 捕捉框的状态，默认是 1 白色的框, 2 红色框, 3 绿色框

  getCaptureStatus() {
    return {
      ERROR: 1,
      SUCCESS: 2,
      INFO: 3
    };
  }

  setCaptureStatus(status: number) {
    this.captureStatus = status;
  }

  /** =============== 捕捉框相关 end ============== */

  // 检测人脸的坐标
  checkFacePosition(rect: FaceModel) {
    const xCondition = rect.x >= 55 && rect.x <= 120;
    const yCondition = rect.y >= 75 && rect.y <= 125;
    const wCondition = rect.width > 150;
    const hCondition = rect.width > 150;
    if (
      rect.total === 1 &&
      xCondition &&
      yCondition &&
      wCondition &&
      hCondition
    ) {
      return true;
    } else {
      return false;
    }
  }

  pause() {
    this.trackerTask?.stop();
    this.videoElement?.pause();
  }

  continue() {
    this.tracker?.start();
    this.videoElement?.start();
  }

  async openUserMedia() {
    try {
      const constraints = {
        audio: false,
        video: {
          width: this.canvasWidth,
          height: this.canvasHeight,
          facingMode: "user" // 前置摄像头
        }
      };
      Video.getUserMedia(constraints)
        .then((stream: any) => {
          this.init();
          this.streamIns = stream;
          if ("srcObject" in this.videoElement) {
            this.videoElement.srcObject = stream;
          } else {
            // @ts-ignore
            this.videoElement.src = URL.createObjectURL(stream);
          }
          this.videoElement.play();
          setTimeout(() => {
            this.initTracker();
          }, 1000);
        })
        .catch(e => {
          // 获取权限失败
          this.$emit("update:permission", false);
        });
    } catch (e) {
      console.log(e);
    }
  }

  takePhoto() {
    return new Promise((resolve, reject) => {
      if (this.canvasContext) {
        this.canvasContext.drawImage(
          this.videoElement,
          0,
          0,
          this.videoWidth,
          this.videoHeight
        );
        this.canvas.toBlob((blob: any) => {
          this.file = new File([blob], "FaceImage", { type: "image/jpg" });
          this.$emit("verify", this.file);
          const reader = new FileReader();
          reader.readAsDataURL(this.file);
          reader.onload = () => {
            this.imgData = reader.result;
          };
          resolve(true);
        }, "image/jpg");
        resolve(true);
      } else {
        reject(false);
      }
    });
  }

  async userMediaFaceVerify() {
    console.log("faceVerify");
    await this.takePhoto();
  }

  // 清空 canvas
  clearCanvas() {
    console.log("clearCanvas");
    this.canvasContext.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
  }

  // 清除自动识别人脸
  clearTracker() {
    clearTimeout(this.removePhotoID);
    this.trackerTask?.stop();
    this.tracker = null;
    // 初始化是否找到脸的状态
    this.findFace = false;
  }

  // 清除流相关
  clearStreamIns() {
    if (this.streamIns) {
      this.streamIns.enabled = false;
      this.streamIns.getTracks()[0]?.stop();
      this.streamIns.getVideoTracks()[0]?.stop();
    }
    this.streamIns = null;
  }
  // 关闭流相关
  close() {
    try {
      this.clearStreamIns();
      this.clearTracker();
    } catch (e) {
      console.log("🚀 ~ ComUserMediaVerify ~ close ~ e:", e);
    }
  }

  beforeDestroy() {
    this.close();
  }
}
